import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  BrandSmartLookupPurchase,
  brandsmartVerifyOtp,
  brandsmartSendOtp,
} from '../../services/brandsmart.api';

interface Props {
  contact: string;
  contactType: 'email' | 'phone';
  onBack: () => void;
  onVerified: (purchases: BrandSmartLookupPurchase[]) => void;
}

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

export default function LocateStep2Verify({ contact, contactType, onBack, onVerified }: Props) {
  const { t } = useTranslation('brandsmart');
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    if (resendSeconds <= 0) return;
    const t = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSeconds]);

  function handleDigit(idx: number, val: string) {
    // Handle paste (multi-digit)
    if (val.length > 1) {
      const clean = val.replace(/\D/g, '').slice(0, OTP_LENGTH);
      setDigits(() => {
        const next = Array(OTP_LENGTH).fill('');
        clean.split('').forEach((c2, i2) => { next[i2] = c2; });
        return next;
      });
      inputRefs.current[Math.min(clean.length, OTP_LENGTH - 1)]?.focus();
      if (clean.length === OTP_LENGTH) handleVerify();
      return;
    }
    // Single digit
    const clean = val.replace(/\D/g, '');
    const next = [...digits];
    next[idx] = clean;
    setDigits(next);
    if (clean && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };
  function handleKeyDown(idx: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = '';
        setDigits(next);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
        const next = [...digits];
        next[idx - 1] = '';
        setDigits(next);
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[idx + 1]?.focus();
    }
  };

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((c2, i2) => { next[i2] = c2; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    if (pasted.length === OTP_LENGTH) handleVerify();
  };
  async function handleVerify() {
    const otp = digits.join('');
    if (otp.length < OTP_LENGTH) return;
    setLoading(true);
    try {
      const result = await brandsmartVerifyOtp({ identifier: contact, otp });
      onVerified(result.purchases);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('locateStep2.invalidCode');
      toast.error(msg);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await brandsmartSendOtp({ identifier: contact });
      setResendSeconds(RESEND_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      toast.success(t('locateStep2.newCodeSent'));
      inputRefs.current[0]?.focus();
    } catch {
      toast.error(t('locateStep2.resendError'));
    } finally {
      setResending(false);
    }
  }

  const otp = digits.join('');
  const maskedContact =
    contactType === 'email'
      ? contact.replace(/(.{2}).+(@.+)/, '$1****$2')
      : contact.replace(/(\+?\d{1,3})\s?\d+(\d{4})/, '$1 ****$2');

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>
        <h2 className="text-xl font-semibold mb-1">{t('locateStep2.heading')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('locateStep2.bodyBefore')} <strong className="text-foreground">{maskedContact}</strong>
          {contactType === 'phone' && (
            <span className="text-amber-600"> <span dangerouslySetInnerHTML={{ __html: t('locateStep2.demoSuffix') }} /></span>
          )}
        </p>
      </div>

      {/* OTP boxes */}
      <div className="flex gap-3 justify-center">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            autoFocus={i === 0}
            className={[
              'h-14 w-12 text-center text-xl font-semibold border-2 rounded-lg bg-background text-zinc-900',
              'focus:outline-none focus:border-primary transition-colors',
              d ? 'border-primary' : 'border-border',
            ].join(' ')}
          />
        ))}
      </div>

      <button
        onClick={handleVerify}
        disabled={loading || otp.length < OTP_LENGTH}
        className="w-full bg-primary text-primary-foreground rounded-lg px-4 py-3 text-sm font-medium disabled:opacity-50 transition-opacity"
      >
        {loading ? t('locateStep2.verifying') : t('locateStep2.verifyCode')}
      </button>

      {/* Resend */}
      <div className="text-center">
        {resendSeconds > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('locateStep2.resendIn', { seconds: resendSeconds })}
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="flex items-center gap-1.5 text-sm text-primary mx-auto hover:underline"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
            {resending ? t('locateStep2.sending') : t('locateStep2.resend')}
          </button>
        )}
      </div>
    </div>
  );
}
